import type { HubSpotFieldMapping, HubSpotMappingConfig } from '@/types/hubspot'

/** The OrbitalX field the lead source label travels on. Mirrors HubSpotMappingDefaults.LeadSourceField. */
export const LEAD_SOURCE_FIELD = 'lead_source'

/**
 * Our own free-text HubSpot property for the label (ORB-1056). Mirrors HubSpotConstants.CustomProperties.LeadSource.
 * The sync creates it in the account before its first write, so it can be missing from the account's property
 * list until a contact has been pushed.
 */
export const LEAD_SOURCE_PROPERTY = 'orbitalx_lead_source'
export const LEAD_SOURCE_PROPERTY_LABEL = 'OrbitalX Lead Source'
export const DEFAULT_LEAD_SOURCE_VALUE = 'OrbitalX Integration'

/** HubSpot's Original Traffic Source and its drill-downs: HubSpot's own, never written. Mirrors StandardProperties.LeftToHubSpot. */
export const LEFT_TO_HUBSPOT = new Set(['hs_analytics_source', 'hs_analytics_source_data_1', 'hs_analytics_source_data_2'])

// Case-insensitive, as the server compares them.
export const isLeadSourceProperty = (name: string | null | undefined) => name?.toLowerCase() === LEAD_SOURCE_PROPERTY

export function findLeadSourceMapping(config: HubSpotMappingConfig): HubSpotFieldMapping | undefined {
    return config.FieldMappings.find(m => m.OrbitalXField.toLowerCase() === LEAD_SOURCE_FIELD)
}

/** Returns the config with the Lead Source mapping changed, adding the default mapping if it was removed. */
export function withLeadSourceMapping(
    config: HubSpotMappingConfig,
    updates: Partial<HubSpotFieldMapping>
): HubSpotMappingConfig {
    const existing = findLeadSourceMapping(config)
    if (existing) {
        return {
            ...config,
            FieldMappings: config.FieldMappings.map(m => (m === existing ? { ...m, ...updates } : m)),
        }
    }
    const added: HubSpotFieldMapping = {
        OrbitalXField: LEAD_SOURCE_FIELD,
        DisplayLabel: 'Lead Source',
        Description: 'Origin of the contact',
        HubSpotProperty: LEAD_SOURCE_PROPERTY,
        IsEnabled: true,
        IsRequired: false,
        IsSystemMapped: false,
        FixedValue: DEFAULT_LEAD_SOURCE_VALUE,
        ...updates,
    }
    return { ...config, FieldMappings: [...config.FieldMappings, added] }
}
